/**
 * Workspace Outils — post-traitement : upscale image (raffineur hires),
 * détourage d'arrière-plan, amélioration vidéo (GFPGAN/Real-ESRGAN),
 * transcription Whisper.
 */
import { APIClient } from "../../api/client.js";
import { accordion } from "../../components/Accordion.js";
import { generateButton } from "../../components/GenerateButton.js";
import { createReadinessPanel } from "../../components/ReadinessPanel.js";
import { toast } from "../../components/Toast.js";
import { h } from "../../components/dom.js";
import { runOperation } from "../../services/execution.js";
import { firstArtifactUrl } from "../../services/hosted-jobs.js";
import type { WorkspaceDef } from "../../shell/types.js";
import { store } from "../../state/store.js";
import { sourceImageUpload } from "../image/SourceUploads.js";

type ToolId = "upscale" | "remove-bg" | "video-upscale" | "transcribe";

// `family`: the model family behind the tool (app/core/family_models.py),
// whose readiness the tool shows; the image upscale uses the image model.
const TOOLS: { id: ToolId; label: string; family: string }[] = [
	{ id: "upscale", label: "Agrandir une image", family: "" },
	{ id: "remove-bg", label: "Détourage", family: "background_remove" },
	{ id: "video-upscale", label: "Améliorer une vidéo", family: "video_upscale" },
	{ id: "transcribe", label: "Transcription", family: "transcribe" },
];

export const toolsWorkspace: WorkspaceDef = {
	id: "tools",
	title: "Outils",
	icon: "tools",
	build(container) {
		container.appendChild(h("h2", { class: "workspace-title" }, "Outils"));

		let tool: ToolId = "upscale";
		const selector = h(
			"div",
			{ class: "task-selector" },
			...TOOLS.map((t) =>
				h(
					"button",
					{
						class: "task-pill",
						type: "button",
						"data-tool": t.id,
						onclick: () => setTool(t.id),
					},
					t.label
				)
			)
		);

		// Upscale image : source + facteur + prompt/seed (raffineur diffusion).
		const upscaleSource = sourceImageUpload({ label: "Image à agrandir" });
		const upscaleFactor = h(
			"select",
			null,
			...[
				["1.5", "×1.5"],
				["2", "×2"],
				["3", "×3"],
				["4", "×4"],
			].map(([value, label]) => h("option", { value }, label))
		) as HTMLSelectElement;
		const upscalePrompt = h("textarea", {
			rows: 2,
			placeholder: "Ce que montre l'image, pour affiner les détails (facultatif)",
		}) as HTMLTextAreaElement;
		const upscaleSeed = h("input", { type: "number", value: -1, step: 1 }) as HTMLInputElement;

		// Détourage.
		const removeBgSource = sourceImageUpload({ label: "Image à détourer" });

		// Upscale vidéo.
		const videoFile = h("input", {
			type: "file",
			accept: "video/mp4,video/webm",
		}) as HTMLInputElement;
		let videoRef: string | null = null;
		const videoStatus = h("small", { class: "help-text", "aria-live": "polite" });

		// Transcription.
		const transcribeFile = h("input", {
			type: "file",
			accept: "audio/wav,audio/mpeg,audio/flac,audio/ogg,audio/mp4,audio/x-m4a",
		}) as HTMLInputElement;
		const transcribeStatus = h("small", { class: "help-text", "aria-live": "polite" });
		const transcribeLang = h(
			"select",
			null,
			h("option", { value: "" }, "Détection automatique"),
			...[
				["en", "Anglais"],
				["fr", "Français"],
				["es", "Espagnol"],
				["de", "Allemand"],
				["it", "Italien"],
				["pt", "Portugais"],
				["ja", "Japonais"],
				["zh", "Chinois"],
				["ru", "Russe"],
				["ar", "Arabe"],
			].map(([value, label]) => h("option", { value }, label))
		) as HTMLSelectElement;
		const transcribeOutput = h("textarea", {
			rows: 8,
			readOnly: true,
			placeholder: "Le texte transcrit apparaîtra ici…",
		}) as HTMLTextAreaElement;
		let transcribeRef: string | null = null;

		const runButton = generateButton("Lancer", () => void run());
		// What the tool's model still needs on the machine, with its button.
		const readiness = createReadinessPanel();

		const upscaleForm = h(
			"div",
			null,
			upscaleSource.element,
			field("Agrandir", upscaleFactor),
			accordion({
				title: "Réglages avancés",
				children: [
					field("Description (facultatif)", upscalePrompt),
					field("Graine (-1 = au hasard)", upscaleSeed),
				],
			}).element,
			h(
				"p",
				{ class: "help-text" },
				"Les détails sont affinés avec le modèle choisi dans la Bibliothèque."
			)
		);
		const removeBgForm = h("div", null, removeBgSource.element);
		const videoForm = h(
			"div",
			null,
			field("Vidéo à améliorer", h("div", { class: "file-input-row" }, videoFile, videoStatus)),
			h(
				"p",
				{ class: "help-text" },
				"GFPGAN + Real-ESRGAN ×4 : visages restaurés et résolution quadruplée."
			)
		);
		const transcribeForm = h(
			"div",
			null,
			field(
				"Fichier audio",
				h("div", { class: "file-input-row" }, transcribeFile, transcribeStatus)
			),
			field("Langue", transcribeLang),
			field("Texte transcrit", transcribeOutput)
		);
		const forms: Record<ToolId, HTMLElement> = {
			upscale: upscaleForm,
			"remove-bg": removeBgForm,
			"video-upscale": videoForm,
			transcribe: transcribeForm,
		};

		container.append(selector, ...Object.values(forms), readiness.element, runButton.element);

		videoFile.addEventListener("change", () => {
			const file = videoFile.files?.[0];
			if (!file) return;
			videoStatus.textContent = "Envoi…";
			APIClient.uploadVideoSource(file)
				.then((response) => {
					videoRef = response.source_ref;
					videoStatus.textContent = `${(response.size_bytes / (1024 * 1024)).toFixed(1)} Mo`;
				})
				.catch((error: unknown) => {
					videoRef = null;
					videoStatus.textContent = "";
					toast(error instanceof Error ? error.message : "Échec de l'envoi.", "error");
				});
		});

		transcribeFile.addEventListener("change", () => {
			const file = transcribeFile.files?.[0];
			if (!file) return;
			transcribeStatus.textContent = "Envoi…";
			APIClient.uploadAudioSource(file)
				.then((response) => {
					transcribeRef = response.source_ref;
					transcribeStatus.textContent = `${(response.size_bytes / (1024 * 1024)).toFixed(1)} Mo`;
				})
				.catch((error: unknown) => {
					transcribeRef = null;
					transcribeStatus.textContent = "";
					toast(error instanceof Error ? error.message : "Échec de l'envoi.", "error");
				});
		});

		function field(label: string, control: HTMLElement, help?: string): HTMLElement {
			return h(
				"div",
				{ class: "form-group" },
				h("label", { class: "field-label" }, label),
				control,
				help ? h("p", { class: "help-text" }, help) : null
			);
		}

		function setTool(id: ToolId): void {
			tool = id;
			for (const button of selector.querySelectorAll<HTMLButtonElement>(".task-pill")) {
				button.classList.toggle("active", button.dataset.tool === id);
			}
			for (const [formId, form] of Object.entries(forms)) {
				form.classList.toggle("hidden", formId !== id);
			}
			const chosen = TOOLS.find((t) => t.id === id);
			runButton.setLabel(chosen?.label.replace(/^.\s/, "") ?? "Lancer");
			readiness.show(chosen?.family ?? "");
		}
		setTool("upscale");

		async function run(): Promise<void> {
			try {
				switch (tool) {
					case "upscale": {
						const ref = upscaleSource.getRef();
						const selected = store.get().selectedModel;
						const modelId = selected?.id ?? store.get().status?.model.id ?? undefined;
						if (!ref) {
							toast("Ajoutez une image à agrandir.", "warning");
							return;
						}
						if (!modelId) {
							toast("Choisissez d'abord un modèle image (Bibliothèque).", "warning");
							return;
						}
						const payload = {
							source_image: ref,
							scale_factor: Number(upscaleFactor.value) || 2,
							prompt: upscalePrompt.value || "high quality, detailed",
							seed:
								Number(upscaleSeed.value) === -1
									? Math.floor(Math.random() * 2 ** 31)
									: Number(upscaleSeed.value),
							model_id: modelId,
							architecture: selected?.architecture,
						};
						await runOperation({
							kind: "upscale",
							payload,
							hostedFailureMessage: "Échec de l'upscale sur votre ordinateur.",
							runLocal: async () => {
								const response = await APIClient.upscaleImage(payload);
								if (!response.success || !response.image_url) {
									throw new Error(response.error ?? "Échec de l'upscale.");
								}
								if (response.warning) toast(response.warning, "warning");
								setImageResult(
									response.image_url,
									response.filename ?? "upscaled.png",
									response.generation_time ?? undefined
								);
							},
							handleHosted: (result, job) => {
								const url = firstArtifactUrl(result);
								if (!url) throw new Error("L'upscale s'est terminé sans image.");
								const warning = result.data?.warning;
								if (typeof warning === "string" && warning) toast(warning, "warning");
								setImageResult(url, `upscaled-${job.job_id}.png`, result.metrics?.generation_time);
							},
						});
						break;
					}
					case "remove-bg": {
						const ref = removeBgSource.getRef();
						if (!ref) {
							toast("Ajoutez une image à détourer.", "warning");
							return;
						}
						await runOperation({
							kind: "background_remove",
							payload: { source_image: ref },
							hostedFailureMessage: "Échec du détourage sur votre ordinateur.",
							runLocal: async () => {
								const response = await APIClient.removeBackground({ source_image: ref });
								if (!response.success || !response.image_url) {
									throw new Error(response.error ?? "Échec du détourage.");
								}
								setImageResult(
									response.image_url,
									response.filename ?? "cutout.png",
									response.generation_time ?? undefined
								);
							},
							handleHosted: (result, job) => {
								const url = firstArtifactUrl(result);
								if (!url) throw new Error("Le détourage s'est terminé sans image.");
								setImageResult(url, `cutout-${job.job_id}.png`, result.metrics?.generation_time);
							},
						});
						break;
					}
					case "video-upscale": {
						const sourceVideo = videoRef;
						if (!sourceVideo) {
							toast("Ajoutez une vidéo à améliorer.", "warning");
							return;
						}
						await runOperation({
							kind: "video_upscale",
							payload: { source_video: sourceVideo },
							hostedFailureMessage: "Échec de l'amélioration vidéo sur votre ordinateur.",
							runLocal: async () => {
								const response = await APIClient.upscaleVideo({ source_video: sourceVideo });
								if (!response.success || !response.video_url) {
									throw new Error(response.error ?? "Échec de l'amélioration vidéo.");
								}
								setVideoResult(
									response.video_url,
									response.filename ?? "video-upscaled.mp4",
									response.generation_time ?? undefined
								);
							},
							handleHosted: (result, job) => {
								const url = firstArtifactUrl(result);
								if (!url) throw new Error("L'amélioration s'est terminée sans vidéo.");
								setVideoResult(
									url,
									`video-upscaled-${job.job_id}.mp4`,
									result.metrics?.generation_time
								);
							},
						});
						break;
					}
					case "transcribe": {
						const sourceAudio = transcribeRef;
						if (!sourceAudio) {
							toast("Ajoutez un fichier audio à transcrire.", "warning");
							return;
						}
						await runOperation({
							kind: "transcribe",
							payload: {
								source_audio: sourceAudio,
								language: transcribeLang.value || null,
							},
							hostedFailureMessage: "Échec de la transcription sur votre ordinateur.",
							runLocal: async () => {
								const response = await APIClient.transcribeAudio({
									source_audio: sourceAudio,
									language: transcribeLang.value || null,
								});
								if (!response.success || !response.text) {
									throw new Error(response.error ?? "Échec de la transcription.");
								}
								transcribeOutput.value = response.text;
								toast("Transcription terminée.", "success");
							},
							handleHosted: (result) => {
								const text = result.data?.text;
								if (typeof text !== "string" || !text.trim()) {
									throw new Error("La transcription s'est terminée sans texte.");
								}
								transcribeOutput.value = text;
								toast("Transcription terminée.", "success");
							},
						});
						break;
					}
				}
			} catch (error) {
				toast(error instanceof Error ? error.message : "Échec de l'opération.", "error");
			}
		}

		function setImageResult(
			url: string,
			filename: string,
			generationTime: number | undefined
		): void {
			store.setState({
				currentResult: { kind: "image", url, filename, generationTime },
				historyVersion: store.get().historyVersion + 1,
			});
		}
		function setVideoResult(
			url: string,
			filename: string,
			generationTime: number | undefined
		): void {
			store.setState({
				currentResult: { kind: "video", url, filename, generationTime },
				historyVersion: store.get().historyVersion + 1,
			});
		}
	},
};
